import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export type StartInvitesResponse = {
  batchId: string;
  recipients: number;
  streamUrl: string; // e.g. /invites/<batchId>/stream
};

export interface Meeting {
  id?: number;
  associationId: number;
  title: string;
  agenda: string;
  date: string;
  type: string;
}

export interface MeetingState {
  meetings: Meeting[];
  meeting: Meeting | null;
  open: boolean;
  status: "idle" | "loading" | "failed";
  error: string | null;
}

const initialState: MeetingState = {
  meetings: [],
  meeting: null,
  open: false,
  status: "idle",
  error: null,
};

export const fetchMeetings = createAsyncThunk<Meeting[], number>(
  "meeting/fetchMeetings",
  async (associationId) => {
    const response = await axiosInstance.get("/meetings", {
      params: { associationId },
    });
    return response.data;
  }
);

export const createMeeting = createAsyncThunk<Meeting, Omit<Meeting, "id">>(
  "meeting/createMeeting",
  async (newMeeting) => {
    const response = await axiosInstance.post("/meetings", newMeeting);
    return response.data;
  }
);

export const updateMeeting = createAsyncThunk<
  Meeting,
  { id: number; updatedMeeting: Partial<Meeting> }
>("meeting/updateMeeting", async ({ id, updatedMeeting }) => {
  const response = await axiosInstance.put(`/meetings/${id}`, updatedMeeting);
  return response.data;
});

export const deleteMeeting = createAsyncThunk<number, number>(
  "meeting/deleteMeeting",
  async (id) => {
    await axiosInstance.delete(`/meetings/${id}`);
    return id;
  }
);

export const sendMeetingInvites = createAsyncThunk<
  StartInvitesResponse, // return type
  any, // arg type
  { rejectValue: string } // reject payload
>("meetings/sendEmail", async (body, { rejectWithValue }) => {
  try {
    const { data } = await axiosInstance.post<StartInvitesResponse>(
      "/meetings/send-email",
      {
        associationId: body.associationId,
        meetingId: body.meetingId,
        place: body.place,
      }
    );
    return data; // { batchId, recipients, streamUrl }
  } catch (err: any) {
    const msg =
      err?.response?.data?.message ??
      err?.message ??
      "Failed to send meeting email.";
    return rejectWithValue(msg);
  }
});

const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {
    setMeeting(state, action: PayloadAction<Meeting | null>) {
      state.meeting = action.payload;
    },
    openMeetingForm(state, action: PayloadAction<boolean>) {
      state.open = action.payload;
      if (!action.payload) {
        state.meeting = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMeetings.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchMeetings.fulfilled, (state, action) => {
        state.status = "idle";
        state.meetings = action.payload;
      })
      .addCase(fetchMeetings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Tapaamisten haku ei onnistunut";
      })
      .addCase(
        createMeeting.fulfilled,
        (state, action: PayloadAction<Meeting>) => {
          state.meetings.push(action.payload);
        }
      )
      .addCase(
        updateMeeting.fulfilled,
        (state, action: PayloadAction<Meeting>) => {
          const index = state.meetings.findIndex(
            (m) => m.id === action.payload.id
          );
          if (index !== -1) {
            state.meetings[index] = action.payload;
          }
        }
      )
      .addCase(
        deleteMeeting.fulfilled,
        (state, action: PayloadAction<number>) => {
          state.meetings = state.meetings.filter(
            (m) => m.id !== action.payload
          );
        }
      );
  },
});

export const { setMeeting, openMeetingForm } = meetingSlice.actions;

export default meetingSlice.reducer;
